
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Shield, Heart, Users, Trophy, Star, Menu, X } from 'lucide-react';
import { useState } from 'react';

const About = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-30">
        <nav className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-yellow-500 to-yellow-300 rounded transform rotate-12"></div>
              <h1 className="text-2xl font-bold text-yellow-600">GoldenTicketLucky</h1>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6">
              <a href="/" className="text-gray-700 hover:text-yellow-600 transition-colors">Home</a>
              <a href="/about" className="text-yellow-600 font-medium">About</a>
              <a href="/results" className="text-gray-700 hover:text-yellow-600 transition-colors">Results</a>
              
              <a href="/contact" className="text-gray-700 hover:text-yellow-600 transition-colors">Contact</a>
              <div className="relative group">
                <button className="text-gray-700 hover:text-yellow-600 transition-colors">Legal</button>
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                  <a href="/terms" className="block px-4 py-2 text-gray-700 hover:bg-yellow-50">Terms</a>
                  <a href="/privacy" className="block px-4 py-2 text-gray-700 hover:bg-yellow-50">Privacy</a>
                  <a href="/cookies" className="block px-4 py-2 text-gray-700 hover:bg-yellow-50">Cookies</a>
                  <a href="/accessibility" className="block px-4 py-2 text-gray-700 hover:bg-yellow-50">Accessibility</a>
                  <a href="/notices" className="block px-4 py-2 text-gray-700 hover:bg-yellow-50">Notices</a>
                </div>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden mt-4 space-y-2">
              <a href="/" className="block py-2 text-gray-700 hover:text-yellow-600">Home</a>
              <a href="/about" className="block py-2 text-yellow-600 font-medium">About</a>
              <a href="/results" className="block py-2 text-gray-700 hover:text-yellow-600">Results</a>
              <a href="/updates" className="block py-2 text-gray-700 hover:text-yellow-600">Updates</a>
              <a href="/contact" className="block py-2 text-gray-700 hover:text-yellow-600">Contact</a>
              <a href="/terms" className="block py-2 text-gray-700 hover:text-yellow-600">Terms</a>
              <a href="/privacy" className="block py-2 text-gray-700 hover:text-yellow-600">Privacy</a>
            </div>
          )}
        </nav>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-yellow-500 to-yellow-300 py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            About GoldenTicketLucky
          </h1>
          <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
            Australia's most trusted lottery platform, bringing dreams to life while supporting local communities and promoting responsible gaming.
          </p>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
              <p className="text-lg text-gray-700 mb-6">
                Founded with a vision to create Australia's most trustworthy and exciting lottery experience, GoldenTicketLucky has become a household name across the nation. We believe that everyone deserves a chance at their golden opportunity.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                Since our inception, we've awarded over $50 million in prizes to lucky Australians, while contributing more than $5 million to local charities and community projects nationwide.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-3xl font-bold text-yellow-600">50M+</div>
                  <div className="text-gray-700">Prizes Awarded</div>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-3xl font-bold text-yellow-600">100K+</div>
                  <div className="text-gray-700">Happy Winners</div>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-r from-yellow-400 to-yellow-200 p-8 rounded-lg">
              <div className="text-center">
                <Star className="w-16 h-16 text-yellow-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Licensed & Regulated</h3>
                <p className="text-gray-700">
                  Fully licensed under the NSW Lotteries Act and regulated by Australian gaming authorities, ensuring complete transparency and fairness.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <Shield className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-4">Trust & Security</h3>
                <p className="text-gray-700">Bank-level security and complete transparency in all our operations.</p>
              </CardContent>
            </Card>
            
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <Heart className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-4">Community First</h3>
                <p className="text-gray-700">Supporting Australian charities and local communities with every ticket sold.</p>
              </CardContent>
            </Card>
            
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <Users className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-4">Responsible Gaming</h3>
                <p className="text-gray-700">Promoting healthy gaming habits and providing support when needed.</p>
              </CardContent>
            </Card>
            
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <Trophy className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-4">Fair Play</h3>
                <p className="text-gray-700">Independently audited draws ensuring every player has an equal chance.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Community Impact Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Community Impact</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-yellow-50">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Healthcare Support</h3>
                <p className="text-gray-700 mb-4">$1.8M donated to children's hospitals and medical research across Australia.</p>
                <div className="text-4xl font-bold text-yellow-600">$1.8M</div>
              </CardContent>
            </Card>
            
            <Card className="bg-yellow-50">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Education Programs</h3>
                <p className="text-gray-700 mb-4">Supporting schools and educational initiatives in rural and urban communities.</p>
                <div className="text-4xl font-bold text-yellow-600">$1.5M</div>
              </CardContent>
            </Card>
            
            <Card className="bg-yellow-50">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Environment & Wildlife</h3>
                <p className="text-gray-700 mb-4">Funding conservation projects and wildlife protection initiatives.</p>
                <div className="text-4xl font-bold text-yellow-600">$1.2M</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Responsible Gambling Notice */}
      <section className="py-8 bg-gray-100">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-gray-700 mb-4">
            <strong>Play Responsibly.</strong> Gambling should be fun and entertaining. 
            If you're concerned about your gambling, help is available.
          </p>
          <div className="space-x-4">
            <a 
              href="https://gambleaware.nsw.gov.au" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-yellow-600 underline hover:text-yellow-700"
            >
              GambleAware NSW
            </a>
            <a 
              href="https://betstop.gov.au" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-yellow-600 underline hover:text-yellow-700"
            >
              BetStop National Self-Exclusion
            </a>
          </div>
          <p className="text-gray-600 text-sm mt-2">Call 1800 858 858 for gambling help</p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-yellow-500 to-yellow-300">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Ready to Join the GoldenTicketLucky Family?</h2>
          <p className="text-xl text-gray-700 mb-8">Your golden opportunity awaits!</p>
          <Button 
            className="bg-yellow-600 hover:bg-yellow-700 text-white px-8 py-4 text-lg rounded-md transition-all transform hover:scale-105"
            onClick={() => window.location.href = '/'}
          >
            Play Now
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-bold text-yellow-500 mb-4">GoldenTicketLucky</h3>
              <p className="text-gray-300 text-sm">
                Dream big, play responsibly. Your trusted Australian lottery experience.
              </p>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <div className="space-y-2 text-sm">
                <a href="/" className="block text-gray-300 hover:text-yellow-500">Home</a>
                <a href="/about" className="block text-gray-300 hover:text-yellow-500">About Us</a>
                <a href="/results" className="block text-gray-300 hover:text-yellow-500">Draw Results</a>
                <a href="/contact" className="block text-gray-300 hover:text-yellow-500">Contact</a>
              </div>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Legal</h4>
              <div className="space-y-2 text-sm">
                <a href="/terms" className="block text-gray-300 hover:text-yellow-500">Terms & Conditions</a>
                <a href="/privacy" className="block text-gray-300 hover:text-yellow-500">Privacy Policy</a>
                <a href="/cookies" className="block text-gray-300 hover:text-yellow-500">Cookie Policy</a>
                <a href="/accessibility" className="block text-gray-300 hover:text-yellow-500">Accessibility</a>
              </div>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Get Help</h4>
              <div className="space-y-2 text-sm">
                <a href="https://gambleaware.nsw.gov.au" target="_blank" rel="noopener noreferrer" className="block text-gray-300 hover:text-yellow-500">GambleAware</a>
                <a href="https://betstop.gov.au" target="_blank" rel="noopener noreferrer" className="block text-gray-300 hover:text-yellow-500">BetStop</a>
                <p className="text-gray-300">+61 35 9294 105</p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-sm text-gray-300">
            <p>© 2025 GoldenTicketLucky. All rights reserved. | Licensed under NSW Lotteries Act | 18+ only</p>
            <p className="mt-2">
              BetStop — National Self-Exclusion Register. A free tool to opt out of online wagering. Visit{' '}
              <a href="https://betstop.gov.au" target="_blank" rel="noopener noreferrer" className="text-yellow-500 underline">
                betstop.gov.au
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;
